import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, type Player, type Team } from '../../services/api';
import '../PitcherAvailabilityList';

@customElement('roster-manager')
export class RosterManager extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state()
  private teams: Team[] = [];

  @state()
  private selectedTeamId = '';

  @state()
  private roster: Player[] = [];

  @state()
  private unassignedPlayers: Player[] = [];

  @state()
  private loading = true;

  @state()
  private saving = false;

  @state()
  private selectedUnassigned: Set<string> = new Set();

  @state()
  private draggedPlayerId: string | null = null;

  @state()
  private dragOverTarget: 'roster' | 'unassigned' | null = null;

  // Toast notifications
  @state()
  private toast: { type: 'success' | 'error'; message: string } | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.loadTeams();
  }

  private async loadTeams() {
    this.loading = true;
    try {
      const teams = await api.getTeams();
      this.teams = teams || [];
      if (this.teams.length > 0 && !this.selectedTeamId) {
        this.selectedTeamId = this.teams[0].id;
        await this.loadRoster();
      }
    } catch (error) {
      console.error('Failed to load teams:', error);
      this.showToast('error', 'Failed to load teams');
    } finally {
      this.loading = false;
    }
  }

  private async loadRoster() {
    if (!this.selectedTeamId) return;

    this.loading = true;
    try {
      const [roster, unassigned] = await Promise.all([
        api.getTeamRoster(this.selectedTeamId),
        api.getUnassignedPlayers(),
      ]);
      this.roster = roster || [];
      this.unassignedPlayers = unassigned || [];
      this.selectedUnassigned.clear();
      this.requestUpdate();
    } catch (error) {
      console.error('Failed to load roster:', error);
      this.showToast('error', 'Failed to load roster');
    } finally {
      this.loading = false;
    }
  }

  private showToast(type: 'success' | 'error', message: string) {
    this.toast = { type, message };
    setTimeout(() => {
      this.toast = null;
    }, 3000);
  }

  private async handleTeamChange(teamId: string) {
    this.selectedTeamId = teamId;
    await this.loadRoster();
  }

  // Selection handling
  private togglePlayerSelection(playerId: string) {
    if (this.selectedUnassigned.has(playerId)) {
      this.selectedUnassigned.delete(playerId);
    } else {
      this.selectedUnassigned.add(playerId);
    }
    this.requestUpdate();
  }

  private selectAllUnassigned() {
    if (this.selectedUnassigned.size === this.unassignedPlayers.length) {
      this.selectedUnassigned.clear();
    } else {
      this.unassignedPlayers.forEach(p => this.selectedUnassigned.add(p.id));
    }
    this.requestUpdate();
  }

  // Bulk assign selected players
  private async assignSelectedPlayers() {
    if (this.selectedUnassigned.size === 0 || !this.selectedTeamId) return;

    this.saving = true;
    try {
      const playerIds = Array.from(this.selectedUnassigned);
      await api.bulkAssignPlayers(this.selectedTeamId, playerIds);

      // Reload roster
      await this.loadRoster();
      this.showToast('success', `Assigned ${playerIds.length} player(s) to team`);
    } catch (error) {
      console.error('Failed to assign players:', error);
      this.showToast('error', 'Failed to assign players');
    } finally {
      this.saving = false;
    }
  }

  // Drag and drop handlers
  private handleDragStart(e: DragEvent, playerId: string) {
    this.draggedPlayerId = playerId;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', playerId);
    }
  }

  private handleDragEnd() {
    this.draggedPlayerId = null;
    this.dragOverTarget = null;
  }

  private handleDragOver(e: DragEvent, target: 'roster' | 'unassigned') {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
    this.dragOverTarget = target;
  }

  private handleDragLeave() {
    this.dragOverTarget = null;
  }

  private async handleDrop(e: DragEvent, target: 'roster' | 'unassigned') {
    e.preventDefault();
    this.dragOverTarget = null;

    if (!this.draggedPlayerId) return;

    const isFromUnassigned = this.unassignedPlayers.some(p => p.id === this.draggedPlayerId);
    const isFromRoster = this.roster.some(p => p.id === this.draggedPlayerId);

    // Only allow dropping unassigned players onto roster
    if (target === 'roster' && isFromUnassigned) {
      await this.assignPlayerToTeam(this.draggedPlayerId);
    }
    // Note: Removing from roster would require a different API endpoint

    this.draggedPlayerId = null;
  }

  private async assignPlayerToTeam(playerId: string) {
    if (!this.selectedTeamId) return;

    this.saving = true;
    try {
      await api.bulkAssignPlayers(this.selectedTeamId, [playerId]);
      await this.loadRoster();
      this.showToast('success', 'Player assigned to team');
    } catch (error) {
      console.error('Failed to assign player:', error);
      this.showToast('error', 'Failed to assign player');
    } finally {
      this.saving = false;
    }
  }

  render() {
    if (this.loading && this.teams.length === 0) {
      return html`
        <div class="flex justify-center py-8">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
      `;
    }

    if (this.teams.length === 0) {
      return html`
        <div class="alert alert-info">
          <span>Create teams first before managing rosters.</span>
        </div>
      `;
    }

    return html`
      <div class="space-y-4">
        <!-- Header with team selector -->
        <div class="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
          <div>
            <h3 class="text-lg font-semibold">Roster Management</h3>
            <p class="text-sm text-base-content/70">Drag players or use checkboxes to assign</p>
          </div>
          <select
            class="select select-bordered w-full sm:w-auto"
            .value=${this.selectedTeamId}
            @change=${(e: Event) => this.handleTeamChange((e.target as HTMLSelectElement).value)}
          >
            ${this.teams.map(team => html`
              <option value=${team.id}>${team.name}</option>
            `)}
          </select>
        </div>

        <!-- Two column layout -->
        <div class="grid md:grid-cols-2 gap-4">
          <!-- Unassigned players -->
          ${this.renderUnassignedPanel()}

          <!-- Team roster -->
          ${this.renderRosterPanel()}
        </div>

        <!-- Pitcher availability for selected team -->
        ${this.selectedTeamId ? html`
          <div class="collapse collapse-arrow bg-base-200 rounded-lg">
            <input type="checkbox" />
            <div class="collapse-title font-semibold">Pitcher Availability</div>
            <div class="collapse-content">
              <pitcher-availability-list .teamId=${this.selectedTeamId}></pitcher-availability-list>
            </div>
          </div>
        ` : nothing}

        <!-- Toast notification -->
        ${this.toast ? html`
          <div class="toast toast-end">
            <div class="alert ${this.toast.type === 'success' ? 'alert-success' : 'alert-error'}">
              <span>${this.toast.message}</span>
            </div>
          </div>
        ` : nothing}
      </div>
    `;
  }

  private renderUnassignedPanel() {
    return html`
      <div
        class="card bg-base-200 ${this.dragOverTarget === 'unassigned' ? 'ring-2 ring-primary' : ''}"
        @dragover=${(e: DragEvent) => this.handleDragOver(e, 'unassigned')}
        @dragleave=${this.handleDragLeave}
        @drop=${(e: DragEvent) => this.handleDrop(e, 'unassigned')}
      >
        <div class="card-body py-4">
          <div class="flex justify-between items-center mb-2">
            <h4 class="font-semibold">Unassigned Players (${this.unassignedPlayers.length})</h4>
            ${this.selectedUnassigned.size > 0 ? html`
              <button
                class="btn btn-primary btn-xs"
                @click=${this.assignSelectedPlayers}
                ?disabled=${this.saving}
              >
                ${this.saving
                  ? html`<span class="loading loading-spinner loading-xs"></span>`
                  : `Assign ${this.selectedUnassigned.size} →`}
              </button>
            ` : nothing}
          </div>

          ${this.unassignedPlayers.length > 0 ? html`
            <div class="mb-2">
              <label class="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  class="checkbox checkbox-xs"
                  .checked=${this.selectedUnassigned.size === this.unassignedPlayers.length}
                  @change=${this.selectAllUnassigned}
                />
                Select all
              </label>
            </div>

            <div class="space-y-1 max-h-80 overflow-y-auto">
              ${this.unassignedPlayers.map(player => html`
                <div
                  class="flex items-center gap-2 p-2 bg-base-100 rounded-lg cursor-grab hover:bg-base-300 transition-colors ${this.draggedPlayerId === player.id ? 'opacity-50' : ''}"
                  draggable="true"
                  @dragstart=${(e: DragEvent) => this.handleDragStart(e, player.id)}
                  @dragend=${this.handleDragEnd}
                >
                  <input
                    type="checkbox"
                    class="checkbox checkbox-sm"
                    .checked=${this.selectedUnassigned.has(player.id)}
                    @change=${() => this.togglePlayerSelection(player.id)}
                    @click=${(e: Event) => e.stopPropagation()}
                  />
                  <span class="font-mono text-sm text-base-content/70 w-8">
                    ${player.jersey_number ?? '-'}
                  </span>
                  <span class="flex-1 font-medium">${player.name}</span>
                  ${player.is_adhoc ? html`
                    <span class="badge badge-xs badge-ghost">AP</span>
                  ` : nothing}
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-base-content/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16" />
                  </svg>
                </div>
              `)}
            </div>
          ` : html`
            <p class="text-center text-base-content/70 py-4">
              No unassigned players
            </p>
          `}
        </div>
      </div>
    `;
  }

  private renderRosterPanel() {
    const team = this.teams.find(t => t.id === this.selectedTeamId);

    return html`
      <div
        class="card bg-base-200 ${this.dragOverTarget === 'roster' ? 'ring-2 ring-primary' : ''}"
        @dragover=${(e: DragEvent) => this.handleDragOver(e, 'roster')}
        @dragleave=${this.handleDragLeave}
        @drop=${(e: DragEvent) => this.handleDrop(e, 'roster')}
      >
        <div class="card-body py-4">
          <h4 class="font-semibold mb-2">
            ${team?.name || 'Team'} Roster (${this.roster.length})
          </h4>

          ${this.loading ? html`
            <div class="flex justify-center py-4">
              <span class="loading loading-spinner loading-md"></span>
            </div>
          ` : this.roster.length > 0 ? html`
            <div class="space-y-1 max-h-80 overflow-y-auto">
              ${this.roster.map(player => html`
                <div class="flex items-center gap-2 p-2 bg-base-100 rounded-lg">
                  <span class="font-mono text-sm text-base-content/70 w-8">
                    ${player.jersey_number ?? '-'}
                  </span>
                  <span class="flex-1 font-medium">${player.name}</span>
                  ${player.position ? html`
                    <span class="badge badge-sm">${player.position}</span>
                  ` : nothing}
                </div>
              `)}
            </div>
          ` : html`
            <div class="text-center py-8 text-base-content/70 border-2 border-dashed border-base-300 rounded-lg">
              <p>No players on this team</p>
              <p class="text-sm mt-1">Drag players here or select and assign</p>
            </div>
          `}
        </div>
      </div>
    `;
  }
}
